/**
 * Compute smooth vertex normals with UV-seam welding.
 *
 * Three.js's computeVertexNormals() treats each vertex index independently.
 * Vertices that share the same position but have different indices (UV-seam
 * splits) get independent normals, causing hard shading edges at seam
 * boundaries. This is visible after any topology-changing operation:
 * subdivision, mirror, solidify, edit-mode operators, etc.
 *
 * This module provides a single function that computes normals and then
 * welds them at coincident positions, so every call site gets correct
 * smooth shading without duplicating the fix.
 */

import type { BufferGeometry } from "three/webgpu";

/**
 * Compute area-weighted vertex normals and weld them at coincident positions.
 *
 * Drop-in replacement for `geo.computeVertexNormals()` that also averages
 * normals across UV-seam splits so smooth shading is continuous everywhere.
 */
export function computeWeldedNormals(geo: BufferGeometry): void {
  geo.computeVertexNormals();

  const posAttr = geo.getAttribute("position");
  const normAttr = geo.getAttribute("normal");
  if (!posAttr || !normAttr) return;

  const count = posAttr.count;

  // Group vertex indices by quantized position (spatial hashing).
  // Quantize to ~0.0001 precision — tight enough to match true duplicates
  // without merging intentionally distinct vertices.
  const posMap = new Map<string, number[]>();

  for (let i = 0; i < count; i++) {
    const x = posAttr.getX(i);
    const y = posAttr.getY(i);
    const z = posAttr.getZ(i);
    const key = `${Math.round(x * 10000)}|${Math.round(y * 10000)}|${Math.round(z * 10000)}`;
    let list = posMap.get(key);
    if (!list) {
      list = [];
      posMap.set(key, list);
    }
    list.push(i);
  }

  // Average normals across coincident vertices.
  for (const indices of posMap.values()) {
    if (indices.length <= 1) continue;

    let nx = 0,
      ny = 0,
      nz = 0;
    for (const i of indices) {
      nx += normAttr.getX(i);
      ny += normAttr.getY(i);
      nz += normAttr.getZ(i);
    }

    const len = Math.sqrt(nx * nx + ny * ny + nz * nz);
    if (len > 0) {
      nx /= len;
      ny /= len;
      nz /= len;
    }

    for (const i of indices) {
      normAttr.setXYZ(i, nx, ny, nz);
    }
  }

  normAttr.needsUpdate = true;
}
